"use strict";
const isEmpty = require('lodash/isEmpty');
const map = require('lodash/map');
const pickBy = require('lodash/pickBy');
const isString = require('lodash/isString');
const isObject = require('lodash/isObject');
const values = require('lodash/values');
const has = require('lodash/has');
const get = require('lodash/get');
const isNull = require('lodash/isNull');
const forEach = require('lodash/forEach');
const isNil = require('lodash/isNil');
const find = require('lodash/find');
const moment = require('moment');
const cheerio = require('cheerio');
const juice = require('juice');
const scheduler = require('node-schedule');
const { mapAccountsByName } = require('../search/utils');
const { componentClasses, booleanAttributeValues, componentAttributes, getThemeStyle, mobileCss, CAMPAIGN_SCHEDULE_TYPES } = require('./consts');
const { getSanitizeHtml } = require('./html');
const { safeParsing } = require('../utils');
const { timeZoneOptions } = require('../date/consts');
const UNSUPPORTED_JUICE_CSS = `
<style>
        @media screen and (max-device-width: 575px), screen and (max-width: 575px) {
            @supports (-webkit-touch-callout: none) {
                .${componentClasses.MEDIA_QUERY_CLASS} .${componentClasses.NPS_TABLE_CONTAINER} .${componentClasses.TR_COMPONENT}  {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                }
            }
        }
</style>`;
const defaultLogo = 'https://ucarecdn.com/7fd35439-f8bf-4dbe-a476-223e46161ce8/';
const getCampaignLogo = (theme, serviceLogo) => theme?.logo || serviceLogo || defaultLogo;
const totangoWatermarkIcon = 'https://ucarecdn.com/68830186-0ac9-41e9-b247-ba956ed143c1//-/resize/200x/';
const socialValidNames = ['facebook', 'twitter', 'linkedin', 'instagram', 'youtube'];
const storeValidNames = ['googlePlay', 'appStore'];
const baseSocialUrl = {
    facebook: 'https://facebook.com/',
    twitter: 'https://twitter.com/',
    linkedin: 'https://linkedin.com/company/',
    instagram: 'https://instagram.com/',
    youtube: 'https://youtube.com/',
    googlePlay: 'https://play.google.com/store/apps/',
    appStore: 'https://apps.apple.com/',
};
const socialIconsImages = {
    facebook: '25793e86-3583-4550-95ba-9250a8b58177',
    twitter: 'bf9f2ba2-d240-47ac-9600-8599a3f2b981',
    linkedin: 'd63d91f4-3c52-43e3-b555-37888e8b4998',
    instagram: '96c79287-79b9-4c9e-9430-909acebc8940',
    youtube: '785dc9b5-3230-46b7-894d-fadea6bda58c',
    googlePlay: '635d66e5-3920-4d4c-bf5c-827b90524732',
    appStore: 'b2e3b91d-dc37-44ba-b00f-88955330bd4d',
};
const getSocialLinkWithUrl = (socialType, url, styleString, imgBackground, isCircle, resize, width) => {
    if (!has(baseSocialUrl, socialType)) {
        return '';
    }
    const imageExtraStyle = socialType === 'googlePlay' || socialType === 'appStore'
        ? 'border-radius: 10%; background-color: transparent !important;'
        : 'background-color: transparent !important;';
    return `&nbsp;&nbsp; <a href="${url}" target="_blank" class="social-icon" social-in-footer="on"
				style="text-decoration: none; ${styleString} background-color: ${imgBackground || '#757575'}; border-radius: ${isCircle ? '50%' : '5px'};">
				<img width=${width} src="https://ucarecdn.com/${socialIconsImages[socialType]}/${resize ? `-/resize/${resize}x/` : ''}" style="${imageExtraStyle}"/>
			</a> &nbsp;&nbsp;`;
};
const buildSocialIcons = (width, socialIcons, socialIconBackgroundColor, socialIconIsCircle) => {
    const socialIconsLinksAndStore = isString(socialIcons) ? safeParsing(socialIcons) : socialIcons;
    if (!isObject(socialIconsLinksAndStore)) {
        return '';
    }
    const validSocialLinks = pickBy(socialIconsLinksAndStore, (url, type) => !!url && socialValidNames.includes(type));
    const validStoreLinks = pickBy(socialIconsLinksAndStore, (url, type) => !!url && storeValidNames.includes(type));
    if (isEmpty(validSocialLinks)) {
        return '';
    }
    const socialStyleString = 'border-radius: 5px; margin: 0 4px; height: 20px; width: 20px; display: inline-block;';
    const storeStyleString = 'border-radius: 5px; margin: 0 4px; height: 33px; width: 116px; display: inline-block; border-radius: 4px;';
    return `
		<tr class="${componentClasses.SOCIAL_ICONS_COMPONENT} with-company-style">
			<td style="width: ${width};" align="center">
				<table>
					<tr>
						${map(validSocialLinks, (url, type) => `
							<td>
					            ${getSocialLinkWithUrl(type, url, socialStyleString, socialIconBackgroundColor, socialIconIsCircle, 20, '20px')}
							</td>
						`).join('')}
					</tr>
				</table>
				<table>
					<tr>
						${map(validStoreLinks, (url, type) => `
							<td>
					            ${getSocialLinkWithUrl(type, url, storeStyleString, 'initial', false, null, '116px')}
							</td>
						`).join('')}
					</tr>
				</table>
			</td>
		</tr>
	`;
};
const getCampaignBodyWidth = (html) => {
    const $ = cheerio.load(html);
    const wrapperBody = $(`.${componentClasses.TEMPLATE_WRAPPER}`)[0];
    return wrapperBody?.attribs['data-body-width'];
};
// * { box-sizing: border-box; } adds styles to the br which causes juice to add closing break <\br>
// which then gets turned into <br> in the final html
const makeCssInline = (html) => juice(html, { applyWidthAttributes: false }).replaceAll(/<\/br>/g, '');
const watermarkSpacer = (hideWatermark) => (`<tr class="${componentClasses.TR_COMPONENT} watermark-spacer-top" height="20" style="${hideWatermark ? 'display: none !important; mso-hide: all;' : ''}"><td>&nbsp;</td></tr>`);
const watermarkRow = (hideWatermark) => (!hideWatermark ? `
	<tr class="${componentClasses.TR_COMPONENT} ${componentClasses.WATERMARK}" height="20">
		<td align="center" valign="center" class="${componentClasses.STATIC_TD}" colspan="5">
			<a href="http://www.totango.com/?utm_source=campaigns&utm_medium=email&utm_campaign=customers" class="${componentClasses.STATIC_ANCHOR}" target="_blank">
				<img width="149" src="${totangoWatermarkIcon}" />
			</a>
		</td>
	</tr>
` : '');
const getFontLinks = (campaignSettings) => {
    if (!campaignSettings.customFonts) {
        return '';
    }
    const fonts = JSON.parse(campaignSettings.customFonts);
    return fonts.map((font) => `<link href="${font.url}" rel="stylesheet">`).join('\n');
};
/**
 *
 * @param {*} html
 * @param {*} css
 * @param {*} settings
 * @param {*} serviceLogo
 * @param {*} avoidHtmlWrap - if true, returns HTML body, without <html> and <head>
 * @returns
 */
const prepareHtml = (html, css, settings, serviceLogo = '', { trial } = {}, avoidHtmlWrap = false) => {
    const { companyFooter, socialLinks, hideWatermark, theme = {}, footerSettings } = settings;
    try {
        const $ = cheerio.load(html);
        if (css) {
            $('head').append(`
                <style>${getThemeStyle(theme)}</style>
                <style>${mobileCss}</style>
                <style>${css}</style>
                ${getFontLinks(settings)}
            `);
            $(`.${componentClasses.IMAGE_COMPONENT}.${componentAttributes.USE_COMPANY_LOGO}`).find('img').attr('src', getCampaignLogo(theme, serviceLogo));
            // append footer content
            const useCompanyFooterCondition = `[${componentAttributes.USE_COMPANY_FOOTER}=${booleanAttributeValues.ON}]`;
            const showFooterCondition = `[${componentAttributes.SHOW_FOOTER}=${booleanAttributeValues.ON}]`;
            if (companyFooter) {
                const bodyWidthTemplate = $(`.${componentClasses.TEMPLATE_WRAPPER}`).attr(componentAttributes.BODY_WIDTH);
                const bodyWidth = bodyWidthTemplate !== '600' ? bodyWidthTemplate : '600px';
                const companyFooterCheerio = cheerio.load(companyFooter);
                const { socialIconColor, socialIconShape: companySocialIconShape, footerBackground } = footerSettings || {};
                companyFooterCheerio('#unsubscribe a').attr('href', '{{unSubscribeLink}}');
                const selectedCompanyFooter = $(`.${componentClasses.FOOTER_COMPONENT}${showFooterCondition}${useCompanyFooterCondition}`);
                if (!isEmpty(selectedCompanyFooter)) {
                    const existingFooterStyle = selectedCompanyFooter.attr('style') || '';
                    const footerStyleWithBackgroundColor = `${existingFooterStyle} background-color: ${footerBackground || 'transparent'};`;
                    const companySocialIconIsCircle = companySocialIconShape === 'circle';
                    selectedCompanyFooter.attr('style', footerStyleWithBackgroundColor);
                    selectedCompanyFooter.append(`
                    <tr>
                        <td style="width: ${bodyWidth};" align="center">
                            ${companyFooterCheerio.html()}
                        </td>
                    </tr>
					${buildSocialIcons(bodyWidth, socialLinks, socialIconColor, companySocialIconIsCircle)}
                `);
                }
            }
            const WATERMARK_ELEMENT = `
				${watermarkSpacer()}
          		${watermarkRow()}
          		${watermarkSpacer()}
			`;
            if (!hideWatermark) {
                $(`.${componentClasses.FOOTER_COMPONENT}${showFooterCondition}${useCompanyFooterCondition}`).append(WATERMARK_ELEMENT);
            }
            if (trial) {
                const watermarkEl = $(`.${componentClasses.WATERMARK}`);
                const watermarkElImg = $(`.${componentClasses.WATERMARK} img`);
                const bodyWrapperComp = $(`.${componentClasses.BODY_WRAPPER}`);
                if (!watermarkEl.length || !watermarkElImg.length) {
                    const footer = $(`.${componentClasses.FOOTER_COMPONENT}`);
                    if (footer.length) {
                        footer.append(WATERMARK_ELEMENT);
                    }
                    else if (bodyWrapperComp.length) {
                        const parentNodeComp = bodyWrapperComp[0].parentNode;
                        if (parentNodeComp) {
                            $(parentNodeComp).append(`<table>${WATERMARK_ELEMENT}</table>`);
                        }
                    }
                    else {
                        $.root().append(`<table>${WATERMARK_ELEMENT}</table>`);
                    }
                }
            }
            const htmlWithInlineCss = makeCssInline($.html());
            const cheerioHtml = cheerio.load(htmlWithInlineCss);
            cheerioHtml('head').append(UNSUPPORTED_JUICE_CSS);
            return avoidHtmlWrap ? cheerioHtml('body').html() : cheerioHtml.html();
        }
        if (html.indexOf('<body') === -1 && !isEmpty($('style')) && !avoidHtmlWrap) {
            let styleTags = '';
            $('style').each((index, styleContent) => {
                styleTags += `<style>${$(styleContent).html()}</style>`;
            });
            return `<head>${styleTags}</head><body>${$.html()}</body>`;
        }
        return avoidHtmlWrap ? $('body').html() : $.html();
    }
    catch (err) {
        return html;
    }
};
const isSocialIconsExist = (socialIcons = {}) => !values(socialIcons).every(isEmpty);
const getHtmlNodeFromString = (htmlString) => {
    const $ = cheerio.load(htmlString, null, false);
    return $.html();
};
const getFooterHtml = (companyFooter, socialLinks, hideWatermark, socialIconColor, socialIconIsCircle, footerBackgroundColor) => {
    const defaultFooterStyle = 'color: #282828; font-family: Arial; font-size: 12px;';
    const isIconsExist = isSocialIconsExist(socialLinks);
    const companyFooterOutput = getSanitizeHtml(companyFooter);
    return `
      <table class=${componentClasses.FOOTER_COMPONENT} width="600" border="0" cellpadding="0" cellspacing="0" valign="top"
       style="overflow:hidden!important; font-size: 12px; background-color: ${footerBackgroundColor || 'transparent'}">
        <tbody>
          <tr height="6"><td></td></tr>
          <tr height="20"><td class="template-footer" align="center" style="${defaultFooterStyle}">${getHtmlNodeFromString(companyFooterOutput)}</td></tr>
          ${isIconsExist ? `
          <tr>
            <td class="social-icons" align="center">
              <table>
                <tbody>
                  ${buildSocialIcons('100%', socialLinks, socialIconColor, socialIconIsCircle)}
                </tbody>
              </table>
            </td>
          </tr>
          ` : ''}
          ${watermarkSpacer(hideWatermark)}
          ${watermarkRow(hideWatermark)}
          ${watermarkSpacer(hideWatermark)}
        </tbody>
		  </table>
      `;
};
const isSentOnce = (scheduledType) => (scheduledType === CAMPAIGN_SCHEDULE_TYPES.IMMEDIATELY || ((scheduledType === CAMPAIGN_SCHEDULE_TYPES.LATER)));
const calcNextRecurringDate = (scheduledStart, scheduleRule, scheduledTZ) => {
    if (!scheduleRule || !scheduleRule.spec) {
        return moment(scheduledStart);
    }
    const rule = new scheduler.RecurrenceRule();
    forEach(scheduleRule.spec, (value, key) => {
        if (!isNil(value)) {
            rule[key] = value;
        }
    });
    let selectedTimezone;
    if (scheduleRule.useSelectedTz) {
        selectedTimezone = find(timeZoneOptions, { value: scheduledTZ });
    }
    const job = scheduler.scheduleJob({
        start: moment(scheduledStart).subtract(1, 's').toISOString(), // we subtract 1 second since the scheduler package will only start AFTER the given start
        tz: selectedTimezone ? selectedTimezone.dbName : 'Etc/GMT+0',
        rule,
    }, () => { });
    if (!job) {
        return moment();
    }
    const nextDate = moment(job.nextInvocation().toString());
    job.cancel();
    return nextDate;
};
const getScheduledTypeId = (scheduledType) => get(scheduledType, 'id') || scheduledType;
const getActualStartDate = (scheduledStart, scheduledTZ, scheduledType, scheduleRule) => {
    let result;
    if (!scheduledStart) {
        return null;
    }
    const startDate = moment(scheduledStart);
    const scheduledTZId = get(scheduledTZ, 'id') || scheduledTZ;
    if (getScheduledTypeId(scheduledType) === CAMPAIGN_SCHEDULE_TYPES.RECURRING && moment().isBefore(startDate)) {
        result = calcNextRecurringDate(scheduledStart, scheduleRule, scheduledTZ);
    }
    else {
        result = startDate;
    }
    return scheduledTZId ? result.utcOffset(scheduledTZId) : result;
};
const getIsCampaignStarted = (scheduledStart, actualStartDate) => isNull(scheduledStart) || moment().isAfter(actualStartDate);
const isCampaignSentOnce = (campaign) => {
    const { scheduledStart, scheduledType, scheduledTZ } = campaign;
    if (scheduledType === CAMPAIGN_SCHEDULE_TYPES.IMMEDIATELY) {
        return true;
    }
    if (scheduledType === CAMPAIGN_SCHEDULE_TYPES.LATER) {
        const actualStartDate = getActualStartDate(scheduledStart, scheduledTZ);
        return getIsCampaignStarted(scheduledStart, actualStartDate);
    }
    return false;
};
const enrichUsersWithAccountData = (users, accounts, campaign) => {
    /* eslint-disable no-param-reassign */
    const accountMap = mapAccountsByName(accounts);
    return users.map((user) => {
        const accountId = user.account.name;
        const account = accountMap[accountId];
        if (account) {
            user.account = account;
        }
        user.accountId = accountId;
        if (campaign.testEmailAddress) {
            user.testEmailAddress = campaign.testEmailAddress;
        }
        if (campaign.previewForEmail) {
            user.previewForEmail = campaign.previewForEmail;
        }
        return user;
    });
};
module.exports = {
    prepareHtml,
    makeCssInline,
    getCampaignBodyWidth,
    getCampaignLogo,
    isSentOnce,
    getFooterHtml,
    socialIconsImages,
    buildSocialIcons,
    watermarkSpacer,
    watermarkRow,
    defaultBaseSocialUrl: baseSocialUrl,
    calcNextRecurringDate,
    getScheduledTypeId,
    getActualStartDate,
    getIsCampaignStarted,
    isCampaignSentOnce,
    enrichUsersWithAccountData,
};
//# sourceMappingURL=utils.js.map